/**
 * React Query hooks for the Workspace (org Holder) shared asset library
 * (ADR-019). Mirrors `useArcheAssetLibrary.ts` keyed by `holderId`.
 *
 * Endpoints (all under /contributor/workspaces):
 *   GET    /:holderId/asset-library/config
 *   POST   /:holderId/asset-library/{enable,disable}     owner
 *   PATCH  /:holderId/asset-library/settings             admin+
 *   GET    /:holderId/assets
 *   POST   /:holderId/assets/upload-init                 member+
 *   POST   /:holderId/assets/:assetId/finalize
 *   PATCH  /:holderId/assets/:assetId
 *   POST   /:holderId/assets/:assetId/soft-delete
 *   POST   /:holderId/manifests/snapshot                 admin+ ("Publish library")
 *   POST   /:holderId/pins/refresh                       admin+ (re-pin linked arches)
 *   GET    /:holderId/manifests
 *   GET    /:holderId/arches
 *   POST   /:holderId/arches/:archeId                    link
 *   DELETE /:holderId/arches/:archeId                    revoke (soft | hard)
 *   GET    /:holderId/usage
 *   GET    /:holderId/billing/summary                    owner
 *
 * The workspace roster is never fetched here — membership lives on the
 * existing Vault identity routes and the Workspace controls page.
 */

import { skipToken, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ContributorArcheAsset } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { assetContentTypeForFile } from '@/contributor/components/asset-library/format';

export interface WorkspaceAssetListOptions {
  status?: string;
  tag?: string;
  cursor?: string;
  limit?: number;
  sort?: import('@/lib/api').AssetListSortValue;
  q?: string;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useWorkspaceAssetLibraryConfig(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.workspaceAssetLibrary.config(holderId ?? ''),
    queryFn: holderId ? () => api.getWorkspaceAssetLibraryConfig(holderId) : skipToken,
  });
}

export function useWorkspaceAssetsList(holderId: string | null, opts?: WorkspaceAssetListOptions) {
  return useQuery({
    queryKey: queryKeys.workspaceAssetLibrary.assets(
      holderId ?? '',
      opts as Record<string, unknown>,
    ),
    queryFn: holderId ? () => api.listWorkspaceAssets(holderId, opts) : skipToken,
  });
}

export function useWorkspaceAssetManifests(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.workspaceAssetLibrary.manifests(holderId ?? ''),
    queryFn: holderId ? () => api.listWorkspaceAssetManifests(holderId) : skipToken,
  });
}

export function useWorkspaceLinkedArches(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.workspaceAssetLibrary.arches(holderId ?? ''),
    queryFn: holderId ? () => api.listWorkspaceLinkedArches(holderId) : skipToken,
  });
}

export function useWorkspaceAssetUsage(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.workspaceAssetLibrary.usage(holderId ?? ''),
    queryFn: holderId ? () => api.getWorkspaceAssetUsage(holderId) : skipToken,
  });
}

/** Owner-only on the server; pass `enabled: false` for other roles so no 403 is fetched. */
export function useWorkspaceAssetBillingSummary(holderId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.workspaceAssetLibrary.billingSummary(holderId ?? ''),
    queryFn: holderId ? () => api.getWorkspaceAssetBillingSummary(holderId) : skipToken,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations — library state
// ---------------------------------------------------------------------------

function useInvalidateWorkspaceLibrary(holderId: string) {
  const qc = useQueryClient();
  return {
    config: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaceAssetLibrary.config(holderId) }),
    assets: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaceAssetLibrary.assets(holderId) }),
    manifests: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaceAssetLibrary.manifests(holderId) }),
    arches: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaceAssetLibrary.arches(holderId) }),
    usage: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaceAssetLibrary.usage(holderId) }),
    // The ARCHE side reads this workspace's state too: link validity badges
    // and link eligibility derive from the library state, and an open arche
    // page's "Shared from workspaces" grid lists this library's active
    // assets — so library-state AND asset mutations must refresh that
    // namespace.
    contributorArches: () => qc.invalidateQueries({ queryKey: queryKeys.contributorArches.all }),
  };
}

export function useEnableWorkspaceAssetLibrary(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: () => api.enableWorkspaceAssetLibrary(holderId),
    onSuccess: () => {
      void invalidate.config();
      // Enabling makes previously-invalid links valid again (derived, not
      // mirrored) — refresh the arche list so the validity badges update.
      void invalidate.arches();
      void invalidate.contributorArches();
    },
  });
}

export function useDisableWorkspaceAssetLibrary(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: () => api.disableWorkspaceAssetLibrary(holderId),
    onSuccess: () => {
      void invalidate.config();
      // Disabling makes every link invalid (derived, not mirrored) — refresh
      // the arche list so the validity badges update.
      void invalidate.arches();
      void invalidate.contributorArches();
    },
  });
}

export function useUpdateWorkspaceAssetLibrarySettings(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: (input: { membersCanLink: boolean }) =>
      api.updateWorkspaceAssetLibrarySettings(holderId, input),
    onSuccess: () => {
      void invalidate.config();
      // `membersCanLink` changes which workspaces an arche page lists as
      // eligible to link.
      void invalidate.contributorArches();
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — assets
// ---------------------------------------------------------------------------

/**
 * Performs the 3-step upload (init → S3 PUT → finalize) for one file,
 * exactly like `useUploadArcheAsset`. Returns the finalized asset row.
 */
export function useUploadWorkspaceAsset(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: async (input: { file: File; tags?: string[] }): Promise<ContributorArcheAsset> => {
      const contentType = assetContentTypeForFile(input.file);
      if (!contentType) {
        throw new Error('Unsupported file type — use PNG, JPEG, WebP, MP3 or WAV');
      }
      const init = await api.initWorkspaceAssetUpload(holderId, {
        contentType,
        sizeBytes: input.file.size,
        name: input.file.name,
        tags: input.tags,
      });

      // Direct S3 PUT — no auth header (URL is presigned).
      const putRes = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: input.file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed (${putRes.status})`);
      }

      const fin = await api.finalizeWorkspaceAsset(holderId, init.assetId, {
        name: input.file.name,
        tags: input.tags,
      });
      return fin.asset;
    },
    onSuccess: () => {
      void invalidate.assets();
      void invalidate.config();
      void invalidate.usage();
      void invalidate.contributorArches();
    },
  });
}

export function usePatchWorkspaceAsset(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    // The server PATCH schema is `.strict()` — the body must carry ONLY the
    // patchable fields, never the assetId (which travels in the path).
    mutationFn: ({
      assetId,
      ...body
    }: {
      assetId: string;
      name?: string | null;
      altText?: string | null;
      tags?: string[];
    }) => api.patchWorkspaceAsset(holderId, assetId, body),
    onSuccess: () => {
      void invalidate.assets();
      void invalidate.contributorArches();
    },
  });
}

export function useSoftDeleteWorkspaceAsset(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: (assetId: string) => api.softDeleteWorkspaceAsset(holderId, assetId),
    onSuccess: () => {
      void invalidate.assets();
      void invalidate.config();
      void invalidate.usage();
      void invalidate.contributorArches();
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — publish + links
// ---------------------------------------------------------------------------

/**
 * "Publish library": snapshot the current active assets into a new immutable
 * library version. Linked arches pin the latest version at THEIR next
 * publish — this call never changes what a live arche serves.
 */
export function usePublishWorkspaceAssetLibrary(holderId: string) {
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: () => api.snapshotWorkspaceAssetManifest(holderId),
    onSuccess: () => {
      void invalidate.manifests();
      void invalidate.usage();
      // A first publish flips "library not published yet" on every link.
      void invalidate.arches();
    },
  });
}

/**
 * Re-pin the arches that already serve this library onto its latest published
 * version. Unlike every other call in this file this one DOES change what live
 * plugins show, so it moves every linked arche's manifest — hence the broad
 * invalidation, including the per-arche contributor keys.
 */
export function useRefreshWorkspaceAssetPins(holderId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: () => api.refreshWorkspaceAssetPins(holderId),
    onSuccess: () => {
      void invalidate.arches();
      // Pins moved → pin counts per version and the pinned-bytes meter move.
      void invalidate.manifests();
      void invalidate.usage();
      // Any arche detail page showing shared assets is now stale.
      void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.all });
    },
  });
}

export function useLinkArcheToWorkspaceLibrary(holderId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: (archeId: string) => api.linkArcheToWorkspace(holderId, archeId),
    onSuccess: (_data, archeId) => {
      void invalidate.arches();
      void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.workspaces(archeId) });
      void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.sharedAssets(archeId) });
    },
  });
}

export interface RevokeWorkspaceLinkInput {
  archeId: string;
  /** `soft` keeps live plugins serving until they republish; `hard` unpins immediately. */
  mode: 'soft' | 'hard';
  reason?: string;
}

export function useRevokeWorkspaceArcheLink(holderId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateWorkspaceLibrary(holderId);
  return useMutation({
    mutationFn: (input: RevokeWorkspaceLinkInput) =>
      api.revokeWorkspaceArcheLink(holderId, input.archeId, {
        mode: input.mode,
        reason: input.reason,
      }),
    onSuccess: (_data, input) => {
      void invalidate.arches();
      // A hard revoke deletes pins → the pinned-bytes meter and pin counts move.
      void invalidate.usage();
      void invalidate.manifests();
      void qc.invalidateQueries({
        queryKey: queryKeys.contributorArches.workspaces(input.archeId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.contributorArches.sharedAssets(input.archeId),
      });
    },
  });
}
